import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  AlertCircle,
  Clock,
  Users2,
  ShieldAlert,
  LogOut as LogOutIcon,
  ScanLine,
  AlertTriangle,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const todayLabel = () => {
  const d = new Date();
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const displayName = (u) => {
  if (!u?.username) return "";
  const raw = u.username.split("@")[0];
  const parts = raw.split(/[.\s_-]+/).filter(Boolean);
  return parts.map((p) => p[0].toUpperCase() + p.slice(1)).join(" ") || raw;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    isLocationAdmin,
    isSupervisor,
    isAsstSupervisor,
    selectedLocationId,
  } = useAuth();

  const [live, setLive] = useState(null);
  const [locations, setLocations] = useState([]);
  const [absenceAlerts, setAbsenceAlerts] = useState([]);
  const [absenceLoading, setAbsenceLoading] = useState(false);

  const canSeeAbsenceAlerts = isAdmin || isLocationAdmin;

  useEffect(() => {
    api
      .get("/dashboard/today")
      .then((r) => setLive(r.data))
      .catch(() =>
        setLive({
          present_count: 0,
          late_count: 0,
          total_scanned: 0,
        })
      );
  }, [selectedLocationId]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/locations", { params: { only_active: true }, _skipScope: true })
      .then((r) => setLocations(r.data))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!canSeeAbsenceAlerts) return;
    setAbsenceLoading(true);
    api
      .get("/dashboard/absence-alerts")
      .then((r) => setAbsenceAlerts(r.data || []))
      .catch(() => setAbsenceAlerts([]))
      .finally(() => setAbsenceLoading(false));
  }, [canSeeAbsenceAlerts, selectedLocationId]);

  const scopeLabel = isAdmin
    ? selectedLocationId
      ? locations.find((l) => l.id === selectedLocationId)?.name || "Location"
      : "All Locations"
    : (user?.location_name || "your Location");

  const presentToday = live?.present_count ?? 0;
  const lateToday = live?.late_count ?? 0;
  const scannedToday = live?.total_scanned ?? presentToday;
  const scannedOut = live?.scanned_out_count ?? 0;
  const canScan = isSupervisor || isAsstSupervisor || isLocationAdmin;

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="dashboard-page">
      <AppHeader />

      <main className="mx-auto max-w-[1400px] px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4 vision-rise">
          <div>
            <div
              className="text-xs font-semibold uppercase tracking-[0.16em] text-vision-slate/60"
              data-testid="dashboard-date"
            >
              {todayLabel()}
            </div>
            <h1
              className="mt-1 font-display text-3xl font-extrabold text-vision-ink sm:text-4xl"
              data-testid="dashboard-greeting"
            >
              {greeting()}, {displayName(user) || "there"}.
            </h1>
            <p className="mt-1 text-sm text-vision-slate/70">
              Live attendance overview for {scopeLabel}.
            </p>
          </div>

          {canScan && (
            <button
              className="btn-pill bg-[color:var(--vision-orange)] px-5 py-2.5 text-sm font-semibold text-white shadow-card hover:brightness-105 hover:shadow-card-lg"
              data-testid="dashboard-open-attendance"
              onClick={() => navigate("/attendance")}
            >
              <ScanLine className="mr-2 inline h-4 w-4" />
              Open Attendance
            </button>
          )}
        </div>

        {/* HERO — slate KPI card, driven purely by /api/dashboard/today. */}
        <section
          className="relative overflow-hidden rounded-[24px] bg-vision-slate text-white shadow-card-lg grain-overlay vision-rise vision-rise-d1"
          data-testid="dashboard-hero-card"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-24 h-[360px] w-[360px] rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(closest-side, #F79009 0%, transparent 70%)" }}
          />

          <div className="relative grid gap-8 p-8 sm:p-10 md:grid-cols-[1.4fr_1fr]">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
                Total present today
              </div>

              <div
                className="mt-3 font-display font-extrabold leading-[0.95] tracking-tight numeric-tabular text-white"
                style={{ fontSize: "clamp(64px, 9vw, 128px)" }}
                data-testid="dashboard-hero-kpi"
              >
                {presentToday.toLocaleString()}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                <span
                  className="chip"
                  style={{
                    backgroundColor: "rgba(39, 174, 96, 0.18)",
                    color: "#7EE2A8",
                    border: "1px solid rgba(39, 174, 96, 0.35)",
                  }}
                  data-testid="dashboard-hero-delta"
                >
                  ● Live · {scopeLabel}
                </span>
                <span className="text-white/60" data-testid="dashboard-hero-date">
                  {live?.date || "today"}
                </span>
              </div>
            </div>

            {/* Right side: only counters we actually have from the API. */}
            <div className="grid grid-cols-2 gap-4 self-center">
              <HeroTile
                label="Late"
                testId="dashboard-breakdown-late"
                value={lateToday}
              />
              <HeroTile
                label="Total Scanned"
                testId="dashboard-breakdown-scanned"
                value={scannedToday}
              />
              <HeroTile
                label="Scanned Out"
                testId="dashboard-breakdown-out"
                value={scannedOut}
              />
              <HeroTile
                label="Locations in view"
                testId="dashboard-breakdown-locations"
                value={isAdmin && !selectedLocationId ? locations.length : 1}
              />
            </div>
          </div>
        </section>

        {/* Small stat row — only real numbers, scoped by API. */}
        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 vision-rise vision-rise-d2">
          <SmallStat
            testId="dashboard-stat-present"
            label="Present today"
            value={presentToday}
            icon={Users2}
            tint="orange"
          />
          <SmallStat
            testId="dashboard-stat-late"
            label="Late"
            value={lateToday}
            icon={AlertCircle}
            tint="peach"
          />
          <SmallStat
            testId="dashboard-stat-scanned"
            label="Total scanned"
            value={scannedToday}
            icon={Clock}
            tint="navy"
          />
          <SmallStat
            testId="dashboard-stat-out"
            label="Scanned out"
            value={scannedOut}
            icon={LogOutIcon}
            tint="slate"
          />
        </section>

        {canSeeAbsenceAlerts && (
          <AbsenceAlertsCard
            loading={absenceLoading}
            alerts={absenceAlerts}
            scopeLabel={scopeLabel}
          />
        )}
      </main>
    </div>
  );
};

const AbsenceAlertsCard = ({ loading, alerts, scopeLabel }) => (
  <section
    className="mt-6 rounded-[20px] border bg-white shadow-card vision-rise vision-rise-d2"
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
    data-testid="dashboard-absence-alerts-card"
  >
    <header className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4"
      style={{ borderColor: "rgba(52,73,94,0.08)" }}
    >
      <div className="flex items-center gap-3">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{ backgroundColor: "rgba(247, 144, 9, 0.14)", color: "var(--vision-orange)" }}
        >
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-lg font-bold text-vision-ink"
            data-testid="absence-alerts-title"
          >
            Consecutive Absences
          </h2>
          <p className="text-xs text-vision-slate/70">
            Employees absent 3+ working days in a row · {scopeLabel} · auto-deactivates at 5
          </p>
        </div>
      </div>
      <span
        className="chip"
        style={{
          backgroundColor: alerts.length > 0 ? "rgba(230, 74, 25, 0.12)" : "rgba(39, 174, 96, 0.14)",
          color: alerts.length > 0 ? "#B23A1A" : "#2E7D5A",
          border: `1px solid ${alerts.length > 0 ? "rgba(230, 74, 25, 0.35)" : "rgba(39, 174, 96, 0.35)"}`,
        }}
        data-testid="absence-alerts-count"
      >
        {alerts.length} flagged
      </span>
    </header>
    <div className="overflow-x-auto">
      {loading ? (
        <div className="px-6 py-8 text-sm text-vision-slate/70" data-testid="absence-alerts-loading">
          Loading absence alerts…
        </div>
      ) : alerts.length === 0 ? (
        <div className="px-6 py-8 text-sm text-vision-slate/70" data-testid="absence-alerts-empty">
          No employees are currently on a 3+ day absence streak.
        </div>
      ) : (
        <table className="w-full border-collapse text-sm" data-testid="absence-alerts-table">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
              <th className="px-6 py-3">Employee</th>
              <th className="px-6 py-3">Employee ID</th>
              <th className="px-6 py-3">Location</th>
              <th className="px-6 py-3">Department</th>
              <th className="px-6 py-3">Streak</th>
              <th className="px-6 py-3">Last Absent Day</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr
                key={a.employee_uid}
                className="border-t"
                style={{ borderColor: "rgba(52,73,94,0.06)" }}
                data-testid={`absence-alert-row-${a.employee_id}`}
              >
                <td className="px-6 py-3 font-semibold text-vision-ink">{a.name}</td>
                <td className="px-6 py-3 text-vision-slate">{a.employee_id}</td>
                <td className="px-6 py-3 text-vision-slate">{a.location_name || "—"}</td>
                <td className="px-6 py-3 text-vision-slate">{a.department_name || "—"}</td>
                <td className="px-6 py-3">
                  <span
                    className="chip numeric-tabular"
                    data-testid={`absence-alert-streak-${a.employee_id}`}
                    style={{
                      backgroundColor: a.streak >= 4 ? "rgba(230, 74, 25, 0.14)" : "rgba(247, 144, 9, 0.14)",
                      color: a.streak >= 4 ? "#B23A1A" : "var(--vision-orange)",
                      border: `1px solid ${a.streak >= 4 ? "rgba(230, 74, 25, 0.35)" : "rgba(247, 144, 9, 0.35)"}`,
                    }}
                  >
                    {a.streak} days
                  </span>
                </td>
                <td className="px-6 py-3 text-vision-slate numeric-tabular">
                  {a.last_absent_date || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </section>
);

const tintStyles = {
  peach:  { bg: "var(--vision-peach)",     fg: "var(--vision-ink)"    },
  navy:   { bg: "rgba(0, 53, 158, 0.10)",  fg: "var(--vision-navy)"   },
  slate:  { bg: "rgba(52, 73, 94, 0.10)",  fg: "var(--vision-slate)"  },
  orange: { bg: "rgba(247, 144, 9, 0.14)", fg: "var(--vision-orange)" },
};

const HeroTile = ({ label, value, testId }) => (
  <div
    className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm"
    data-testid={testId}
  >
    <div className="text-[11px] uppercase tracking-[0.14em] text-white/60">
      {label}
    </div>
    <div className="mt-2 font-display text-3xl font-bold numeric-tabular">
      {(value ?? 0).toLocaleString()}
    </div>
  </div>
);

const SmallStat = ({ testId, label, value, icon: Icon, tint }) => (
  <div
    className="rounded-2xl border bg-white p-5 shadow-card"
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ backgroundColor: tintStyles[tint].bg, color: tintStyles[tint].fg }}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
      {label}
    </div>
    <div
      className="mt-1 font-display text-3xl font-extrabold text-vision-ink numeric-tabular"
    >
      {(value ?? 0).toLocaleString()}
    </div>
  </div>
);

export default Dashboard;
